import { ownsPilot, type MechData, type PilotData, type RosterState, type TableScene } from "@lancer-vtt/domain";

/**
 * Every pilot the given slot owns, in stable id order (so the picker's tile row doesn't
 * reshuffle across re-renders / snapshots). "Owned" is `ownsPilot`, keyed on the verified
 * logged-in slot id — the real per-player pilot, not an ephemeral connection id. A slot
 * with none → `[]`.
 */
export function ownedPilots(state: RosterState, actorId: string): PilotData[] {
  return Object.values(state.pilots)
    .filter((p) => ownsPilot(state, p.id, actorId))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * The mechs of the slot's currently-selected pilot, or `[]` if nothing is selected (or
 * the selected id is stale — a pilot since deleted or no longer owned). Mechs are nested
 * under the pilot (`PilotData.mechs`); there is no flat/top-level mech map, so "which
 * mechs to show on the desktop" resolves through the selected pilot. Mirrors the
 * stale-selection guard in `index.tsx` (only an owned pilot resolves), so a deleted
 * selection falls back to no mechs rather than a wrong list.
 */
export function selectedPilotMechs(
  state: RosterState,
  actorId: string,
  selectedPilotId: string | null,
): MechData[] {
  if (selectedPilotId === null) return [];
  const selected = ownedPilots(state, actorId).find((p) => p.id === selectedPilotId);
  return selected?.mechs ?? [];
}

/**
 * Where a player lands after the roster loads (`index.tsx`): the shell for their picked pilot, the
 * full-screen picker, or the shell with no pilot. Pilotless shells are two exceptions to the picker:
 * a COMPENDIUM/CODEX deep link, and a player who owns no pilot while a fight is on the table (the
 * stage is for everyone at the table, as the retired `/combat` route admitted them). A player who
 * owns pilots picks one first, fight or not.
 */
export function playerLanding(args: { ownedCount: number; picked: boolean; deepLink: boolean; scene: TableScene }): "shell" | "picker" | "shell-pilotless" {
  const { ownedCount, picked, deepLink, scene } = args;
  if (picked) return "shell";
  if (deepLink || (ownedCount === 0 && scene.kind === "encounter")) return "shell-pilotless";
  return "picker";
}
